/**
 * Viewing rooms, and the one unauthenticated read in the product: a room's cover.
 */
import type {
  CreateViewingRoomRequest,
  RoomGateResponse,
  RoomResponse,
  RoomVisitorsResponse,
  UpdateViewingRoomRequest,
  ViewingRoomSummary,
  ViewingRoomWorksResponse,
  ViewingRoomsResponse,
} from "@ondo/shared";

import { supabase } from "../supabase.ts";
import { request, requestWith, publicGet, send } from "./request.ts";

export const fetchViewingRooms = () =>
  request<ViewingRoomsResponse>("/rooms").then((body) => body.rooms);

export const createViewingRoom = (body: CreateViewingRoomRequest) =>
  request<ViewingRoomSummary>("/rooms", body);

/**
 * Changes a room.
 *
 * The password is a tri-state and this signature is what preserves it: omit the
 * field to leave it alone, send `clearPassword` to remove it, send a string to
 * set one. A `password: null` would collapse the first two.
 */
export const updateViewingRoom = (roomId: string, body: UpdateViewingRoomRequest) =>
  requestWith<ViewingRoomSummary>("PATCH", `/rooms/${roomId}`, body);

export const fetchViewingRoomWorks = (roomId: string) =>
  request<ViewingRoomWorksResponse>(`/rooms/${roomId}/works`).then((body) => body.works);

/** Who has been in. Read on a room's own screen, never for the list. */
export const fetchViewingRoomVisitors = (roomId: string) =>
  request<RoomVisitorsResponse>(`/rooms/${roomId}/visitors`).then((body) => body.visitors);

/** Closes a room without destroying it, or opens it again. */
export const setViewingRoomHidden = (roomId: string, hidden: boolean) =>
  request<{ status: "open" | "hidden" }>(`/rooms/${roomId}/status`, {
    status: hidden ? "hidden" : "open",
  });

/**
 * Retires a room.
 *
 * A soft delete on the server — the visit log outlives the room and the slug is
 * never reissued — and still a delete here, because that is what it is to the
 * person pressing the button.
 */
export const deleteViewingRoom = (roomId: string) =>
  requestWith<{ deleted: true }>("DELETE", `/rooms/${roomId}`);

/**
 * A room's cover: enough to name what is being unlocked, never its contents.
 *
 * Unauthenticated, and it must stay that way — a member and a stranger following
 * the same link have to see the same page.
 */
export const fetchRoomGate = (handle: string, slug: string) =>
  publicGet<RoomGateResponse>(
    `/room/${encodeURIComponent(handle)}/${encodeURIComponent(slug)}`,
  );

/**
 * Going in: checks the password, records the visit, returns the works.
 *
 * A POST because it WRITES — the visit is the feature the collector was
 * promised — and because a password in a query string ends up in browser
 * history and in a Referer header.
 *
 * Sends the caller's token when they have one, so a signed-in visitor is
 * recorded as themselves rather than by an address they had to type.
 */
export const enterRoom = async (
  handle: string,
  slug: string,
  body: EnterRoomBody,
): Promise<RoomResponse> => {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;

  return send<RoomResponse>(
    `/room/${encodeURIComponent(handle)}/${encodeURIComponent(slug)}/open`,
    token
      ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
      : { "Content-Type": "application/json" },
    JSON.stringify(body),
  );
};

/** What the entry form collects. Both are optional; the server decides which it needs. */
export interface EnterRoomBody {
  email?: string | null;
  password?: string | null;
}
